import { app } from "@microsoft/teams-js";
import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

type TeamsState = {
  isInTeams: boolean;
  isInitialized: boolean;
  context: app.Context | null;
};

const TeamsContext = createContext<TeamsState>({
  isInTeams: false,
  isInitialized: false,
  context: null,
});

/**
 * Provider that initializes the Teams JS SDK once and exposes
 * the detection result to the entire component tree.
 */
export const TeamsProvider = ({ children }: PropsWithChildren<unknown>) => {
  const [state, setState] = useState<TeamsState>({
    isInTeams: false,
    isInitialized: false,
    context: null,
  });

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      try {
        await app.initialize();
        const context = await app.getContext();
        if (!cancelled) {
          setState({ isInTeams: true, isInitialized: true, context });
        }
      } catch {
        if (!cancelled) {
          setState({ isInTeams: false, isInitialized: true, context: null });
        }
      }
    };

    init();
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo(() => state, [state]);

  return (
    <TeamsContext.Provider value={value}>{children}</TeamsContext.Provider>
  );
};

/**
 * Returns whether the app is running inside Microsoft Teams
 * and the Teams context object if available.
 */
export const useTeams = (): TeamsState => {
  return useContext(TeamsContext);
};
